<template>
  <div class="reservation-stats-card card-base card-shadow--small mb-20" v-loading="loading">
    <div class="rsc-header">
      <i class="mdi mdi-clipboard-list-outline rsc-title-icon"></i>
      <span class="rsc-title">{{ $t('dashboards.reservationStats.title') }}</span>
      <el-select
        v-if="isCurrentUserAdmin && repId == null"
        v-model="selectedRepId"
        clearable
        size="mini"
        :placeholder="$t('reports.REP_RESERVATIONS.selectRepresentative')"
        class="rsc-rep-select"
      >
        <el-option
          v-for="rep in filterUserWithRoleRep"
          :key="rep.id"
          :label="`${rep.firstName} ${rep.lastName}`"
          :value="rep.id"
        />
      </el-select>
    </div>
    <div class="rsc-body">
      <div class="rsc-stat rsc-green rsc-clickable" @click="navigateToReservations('under120')">
        <div class="rsc-dot rsc-dot--green"></div>
        <div class="rsc-info">
          <div class="rsc-count">{{ stats.green }}</div>
          <div class="rsc-label">{{ $t('dashboards.reservationStats.lessThan120') }}</div>
        </div>
      </div>
      <div class="rsc-divider"></div>
      <div class="rsc-stat rsc-yellow rsc-clickable" @click="navigateToReservations('between120and150')">
        <div class="rsc-dot rsc-dot--yellow"></div>
        <div class="rsc-info">
          <div class="rsc-count">{{ stats.yellow }}</div>
          <div class="rsc-label">{{ $t('dashboards.reservationStats.between120and150') }}</div>
        </div>
      </div>
      <div class="rsc-divider"></div>
      <div class="rsc-stat rsc-red rsc-clickable" @click="navigateToReservations('over150')">
        <div class="rsc-dot rsc-dot--red"></div>
        <div class="rsc-info">
          <div class="rsc-count">{{ stats.red }}</div>
          <div class="rsc-label">{{ $t('dashboards.reservationStats.moreThan150') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reservationAPI from '@/services/API/reservation';
import { mapGetters, mapActions, mapMutations } from 'vuex';

export default {
  name: 'ReservationStatsCard',
  props: {
    repId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      stats: { green: 0, yellow: 0, red: 0 },
      selectedRepId: null,
    };
  },
  computed: {
    ...mapGetters(['isCurrentUserAdmin']),
    ...mapGetters('users', ['filterUserWithRoleRep']),
    effectiveRepId() {
      if (this.repId != null) return this.repId;
      if (this.isCurrentUserAdmin) return this.selectedRepId;
      return null;
    },
  },
  watch: {
    selectedRepId() {
      this.fetchData();
    },
    repId() {
      this.fetchData();
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          intent: false,
          delayed: false,
          alloc: false,
          completed: false,
          active: true,
        };
        if (this.effectiveRepId != null) params.repId = this.effectiveRepId;
        const statsResponse = await reservationAPI.fetchReservationStats(params);
        this.stats = {
          green: Number((statsResponse && statsResponse.green) || 0),
          yellow: Number((statsResponse && statsResponse.yellow) || 0),
          red: Number((statsResponse && statsResponse.red) || 0),
        };
      } catch (e) {
        console.error('ReservationStatsCard error:', e);
      } finally {
        this.loading = false;
      }
    },
    navigateToReservations(bucket) {
      this.RESET_FILTERS();
      this.SET_FILTER_DELAYED(false);
      this.SET_FILTER_INTENT(false);
      if (this.effectiveRepId != null) {
        this.SET_FILTER_SALES_REP([this.effectiveRepId]);
      }
      this.SET_FILTER_AVAILABILITY_DELAY_BUCKETS([bucket]);
      this.$router.push({
        path: '/reservations',
        query: {
          availabilityDelayBucket: bucket,
        },
      }).catch(() => {
        // Ignore duplicate navigation errors.
      });
    },
    ...mapActions('users', ['fetchAdminUsers']),
    ...mapMutations('reservations', ['RESET_FILTERS', 'SET_FILTER_SALES_REP', 'SET_FILTER_AVAILABILITY_DELAY_BUCKETS', 'SET_FILTER_DELAYED', 'SET_FILTER_INTENT']),
  },
  created() {
    if (this.isCurrentUserAdmin) this.fetchAdminUsers();
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.reservation-stats-card {
  padding: 16px 20px;
  min-width: 260px;

  .rsc-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;

    .rsc-title-icon {
      font-size: 20px;
      color: #909399;
    }

    .rsc-title {
      font-weight: bold;
      font-size: 14px;
      color: #303133;
      flex: 1;
    }

    .rsc-rep-select {
      width: 180px;
    }
  }

  .rsc-body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .rsc-stat {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    flex: 1;

    &:first-child {
      padding-left: 0;
      padding-top: 0;
    }
    &:last-child {
      padding-right: 0;
      padding-bottom: 0;
    }
  }

  .rsc-clickable {
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.15s;

    &:hover .rsc-count,
    &:hover .rsc-label {
      text-decoration: underline;
    }
  }

  .rsc-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex-shrink: 0;

    &--green  { background-color: #67c23a; }
    &--yellow { background-color: #e6a23c; }
    &--red    { background-color: #f56c6c; }
  }

  .rsc-count {
    font-size: 22px;
    font-weight: bold;
    line-height: 1;
    color: #303133;
  }

  .rsc-label {
    font-size: 12px;
    color: #909399;
    margin-top: 2px;
    white-space: nowrap;
  }

  .rsc-divider {
    width: 100%;
    height: 1px;
    background: #ebeef5;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .rsc-body {
      flex-direction: row;
      align-items: center;
      flex-wrap: nowrap;
    }

    .rsc-stat {
      padding: 0 10px;
      min-width: 0;

      &:first-child {
        padding-left: 0;
        padding-top: 0;
      }

      &:last-child {
        padding-right: 0;
        padding-bottom: 0;
      }
    }

    .rsc-divider {
      width: 1px;
      height: 40px;
    }
  }
}
</style>
